import { CHDeviceMetadataState, initialMetadataState } from "@app/project/store/clarehome.state";
import * as ProjectSelectors from "@app/project/store/selectors/projects.selectors";
import * as RouterSelectors from "@app/root-store/selectors/root-store.selectors";
import { LocalizerService } from "@app/shared/services/localization/localizer.service";
import { createFeatureSelector, createSelector } from "@ngrx/store";

const selectState = createFeatureSelector<CHDeviceMetadataState>("chdevicemetadata");

const installHelpKey = "clarehome_device_install_help";
const identifierKey = "clarehome_device_identifier";

export const selectChDeviceMetadataState = createSelector(
    selectState,
    ProjectSelectors.getUuid,
    (state: CHDeviceMetadataState, uuid: string): CHDeviceMetadataState => (state?.uuid ? state : initialMetadataState(uuid))
);

export const getChDeviceMetadata = createSelector(selectChDeviceMetadataState, (metadata: any) => metadata.result || {});

const _sortArray = (services: any[]): any[] => {
    if (services) {
        return Object.values(services).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return [];
};

export const getChDeviceCategoryList = createSelector(getChDeviceMetadata, (meta: any) =>
    meta?.[installHelpKey] ? Object.keys(meta[installHelpKey]).sort() : undefined
);

export const getChDeviceCategoryData = createSelector(getChDeviceMetadata, (meta: any) => meta?.[installHelpKey] || undefined);

export const getChDeviceCategoryHelpText = createSelector(
    getChDeviceMetadata,
    RouterSelectors.selectUrlParam("category"),
    (meta: any, category: string) => meta?.[installHelpKey]?.[category] || {}
);

export const getChDeviceCategoryInstallInfo = createSelector(
    getChDeviceCategoryHelpText,
    RouterSelectors.selectUrlParam("addMethod"),
    (meta: any, index: string) => meta?.deviceTypes?.[index] || {}
);

export const getInstalledChDeviceMetadata = createSelector(getChDeviceMetadata, (meta: any) => meta?.[identifierKey] || undefined);

export const getChDeviceMetaDataSorted = createSelector(getChDeviceMetadata, (meta: any) => {
    const newDeviceInstallHelpMeta = [];

    if (meta) {
        const originalDeviceInstallHelpMeta = meta[installHelpKey] || {};
        const categories = Object.keys(originalDeviceInstallHelpMeta);
        if (categories?.length) {
            categories.forEach(category => {
                let tmp;
                const originalCategoryObj = originalDeviceInstallHelpMeta[category];
                const otherAddMethodInfo = originalCategoryObj?.otherAddMethods;
                if (originalCategoryObj.deviceTypes.length > 1) {
                    const newCategoryData = [];
                    originalCategoryObj.deviceTypes.forEach(deviceType => {
                        if (deviceType.model !== "Nest Thermostat") {
                            let key = "";
                            if (deviceType.manufacturer && deviceType.model) {
                                key = deviceType.manufacturer + " - " + deviceType.model;
                            } else if (!deviceType.manufacturer && deviceType.model) {
                                key = "Generic - " + deviceType.model;
                            }
                            const catData = {
                                key,
                                name: key,
                                info: deviceType,
                            };
                            newCategoryData.push(catData);
                        }
                        if (otherAddMethodInfo && deviceType?.addMethodOthers?.[0]) {
                            Object.assign(otherAddMethodInfo?.[deviceType?.addMethodOthers?.[0]]?.template, deviceType.template);
                        }
                    });

                    const children = _sortArray(newCategoryData);
                    if (otherAddMethodInfo) {
                        otherAddMethodInfo.templates = [];
                        children.forEach(child => {
                            otherAddMethodInfo.templates.push(child.info.template);
                        });
                    }

                    tmp = {
                        key: category.toLowerCase(),
                        name: LocalizerService.getDeviceCategory(category),
                        children: _sortArray(newCategoryData),
                        otherAddMethodInfo,
                    };
                } else {
                    tmp = {
                        key: category.toLowerCase(),
                        name: LocalizerService.getDeviceCategory(category),
                        info: originalCategoryObj.deviceTypes[0],
                    };
                }
                newDeviceInstallHelpMeta.push(tmp);
            });
            newDeviceInstallHelpMeta.push({
                name: "Z-Wave",
                key: "Z-Wave",
                info: {
                    addMethod: "zwave",
                    helpText: "",
                },
            });
        }
    }
    return _sortArray(newDeviceInstallHelpMeta);
});

export const getChDeviceCategoryMetaDataSorted = createSelector(
    getChDeviceMetaDataSorted,
    RouterSelectors.selectUrlParam("category"),
    RouterSelectors.selectUrlParam("otherAdd"),
    (meta: any, category: string, otherAddMethod: string) => {
        if (category) {
            const categoryData = meta?.find(metaCategory => metaCategory.key === decodeURIComponent(category).toLowerCase());
            if (categoryData) {
                if (otherAddMethod) {
                    return categoryData.otherAddMethodInfo;
                }
                return categoryData.children;
            }
        } else {
            return meta;
        }
    }
);

export const getChDeviceAddMethodMetaDataSorted = createSelector(
    getChDeviceCategoryMetaDataSorted,
    RouterSelectors.selectUrlParam("addMethod"),
    RouterSelectors.selectUrlParam("otherAdd"),
    (meta: any, key: string, otherAddMethod: string) => {
        if (otherAddMethod) {
            const metaToReturn = meta[otherAddMethod];
            Object.assign(metaToReturn.template, meta.templates[key]);
            return metaToReturn || {};
        }
        const data = meta.filter(item => (item.info?.id ? item.info?.id === key : item.key === key));
        return data[0]?.info || {};
    }
);

export const getInstallHelpTextByCategory = (category: string, protocolAdapterName: string) =>
    createSelector(getChDeviceMetadata, (meta: any) => {
        // TODO: remove in a future release once Equator is updated
        let serviceCategory = category;
        if (serviceCategory === "CCTV" && !meta?.[installHelpKey]?.[serviceCategory]) {
            serviceCategory = "Cameras";
        }
        return meta && meta[installHelpKey]
            ? (meta[installHelpKey][serviceCategory] || []).deviceTypes?.filter(
                  deviceType => deviceType.ProtocolAdapterName === protocolAdapterName
              ) || {}
            : {};
    });

export const selectDeviceMetadataCallState = createSelector(selectChDeviceMetadataState, (state: CHDeviceMetadataState) => state.callState);
